import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldAlert, Trash2 } from "lucide-react";
import { adminRevealSensitive, adminDeleteApplication, apiError } from "../../lib/api";
import { adminFieldCls } from "./AdminAuth";

const LABELS = {
  dob: "Date of birth", ssn: "SSN", cdib_number: "CDIB #", doc_number: "DOC #",
  pending_charges: "Pending charges", criminal_history: "Criminal history",
  probation_or_parole: "Probation or parole", officer_name: "Officer name", officer_phone: "Officer phone",
};

function Row({ label, value }) {
  return (
    <div className="flex gap-3">
      <dt className="font-medium text-brand-muted min-w-[140px]">{label}</dt>
      <dd className="text-brand-charcoal break-words">{value}</dd>
    </div>
  );
}

export function SensitivePanel({ app }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const present = app.sensitive_present || [];

  const reveal = async () => {
    setLoading(true);
    try { setData(await adminRevealSensitive(app.id)); }
    catch (err) { toast.error(apiError(err, "Could not reveal")); } finally { setLoading(false); }
  };

  const meds = (data?.medications || []).filter((m) => m.name);
  return (
    <section className="mt-8 rounded-2xl border border-brand-rose/40 bg-brand-rose/5 p-5" data-testid="admin-sensitive-panel" aria-labelledby="sensitive-heading">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 id="sensitive-heading" className="font-serif text-lg text-brand-charcoal inline-flex items-center gap-2">
          <ShieldAlert aria-hidden="true" className="w-4 h-4 text-brand-rose" /> Protected information
        </h3>
        {data ? (
          <button type="button" onClick={() => setData(null)} data-testid="admin-hide-sensitive"
            className="inline-flex items-center gap-1.5 text-sm text-brand-teal font-medium">
            <EyeOff aria-hidden="true" className="w-4 h-4" /> Hide
          </button>
        ) : (
          <button type="button" onClick={reveal} disabled={loading || !present.length} data-testid="admin-reveal-sensitive"
            className="inline-flex items-center gap-1.5 text-sm bg-brand-teal text-white rounded-full px-4 py-2 font-medium hover:bg-brand-tealDark disabled:opacity-50">
            <Eye aria-hidden="true" className="w-4 h-4" /> {loading ? "Revealing…" : "Reveal (logged)"}
          </button>
        )}
      </div>
      <p className="text-xs text-brand-muted mt-1">Encrypted at rest. Each reveal is recorded in the activity log.</p>
      <p className="text-xs text-brand-charcoal mt-2" data-testid="admin-privacy-ack-status">
        Privacy acknowledgement: {app.privacy_ack_at
          ? `given ${new Date(app.privacy_ack_at).toLocaleString()} (notice ${app.privacy_notice_version})`
          : "not recorded — submitted before the privacy notice existed"}
      </p>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm mt-4" data-testid="admin-sensitive-values">
        <Row label="SSN" value={data ? data.ssn || "—" : app.ssn_masked || "—"} />
        {Object.entries(LABELS).filter(([k]) => k !== "ssn").map(([k, label]) => (
          <Row key={k} label={label} value={data ? data[k] || "—" : present.includes(k) ? "•••••• (protected)" : "—"} />
        ))}
        <Row label="Medications" value={data ? (meds.length ? meds.map((m) => `${m.name} — ${m.dosage}`).join("; ") : "—")
          : present.includes("medications") ? "•••••• (protected)" : "—"} />
      </dl>
    </section>
  );
}

export function DeleteApplication({ app, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const expected = app.id.slice(0, 8).toUpperCase();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await adminDeleteApplication(app.id, code);
      const f = r.files || {};
      toast.success(f.pending ? `Application deleted. ${f.pending} document(s) locked; storage erase will need a retry.` : `Application and ${f.purged || 0} document(s) permanently erased`);
      onDeleted();
    }
    catch (err) { toast.error(apiError(err, "Delete failed")); } finally { setLoading(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="admin-delete-application"
        className="inline-flex items-center gap-1.5 text-sm text-red-700 border border-red-200 rounded-full px-4 py-2 hover:bg-red-50">
        <Trash2 aria-hidden="true" className="w-4 h-4" /> Delete application
      </button>
    );
  }
  return (
    <form onSubmit={submit} role="alertdialog" aria-labelledby="del-title" aria-describedby="del-desc"
      className="mt-4 w-full rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="admin-delete-dialog">
      <h3 id="del-title" className="font-medium text-red-800">Permanently delete this application?</h3>
      <p id="del-desc" className="text-sm text-red-800/90 mt-1">
        This permanently erases the record and its uploaded documents. It cannot be undone. Type <b className="font-mono">{expected}</b> to confirm.
      </p>
      <label htmlFor="del-code" className="sr-only">Confirmation code</label>
      <input id="del-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} className={adminFieldCls + " mt-3 font-mono"}
        data-testid="admin-delete-confirm-input" />
      <div className="mt-3 flex gap-3 justify-end">
        <button type="button" onClick={() => { setOpen(false); setCode(""); }} className="text-sm text-brand-muted px-3 py-2" data-testid="admin-delete-cancel">Cancel</button>
        <button type="submit" disabled={loading || code.trim().toUpperCase() !== expected} data-testid="admin-delete-confirm"
          className="text-sm bg-red-700 text-white rounded-full px-4 py-2 font-medium disabled:opacity-50">
          {loading ? "Deleting…" : "Delete permanently"}
        </button>
      </div>
    </form>
  );
}
